import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE } from '@/lib/utils.ts';
import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { buildDefaultTableContent, defaultTableContent } from '../../tiptap-ui/table-dropdown-menu/table-dropdown-menu';
import { ZyExampleNodeView } from './zyexample-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyExample: {
            insertZyExample: (type: ZyExampleType, options?: { caption?: string; rows?: number; cols?: number }) => ReturnType;
        };
    }
}

export type ZyExampleType = 'example' | 'figure' | 'enum' | 'table' | 'try';

export const ZyExampleNodeHeadless = Node.create({
    name: 'zyExample',

    content: function () {
        return getBasicContentSchema({ additionalContentTypes: ['zyInlineEnum'] });
    },

    group: 'block',

    draggable: true,

    isolating: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('id') || null;
                },
                renderHTML: attributes => {
                    if (!attributes.id) {
                        return {};
                    }
                    return { id: attributes.id };
                },
            },
            caption: {
                default: '',
                parseHTML: element => {
                    return element.getAttribute('caption') || '';
                },
                renderHTML: attributes => {
                    if (!attributes.caption) {
                        return {};
                    }
                    return { caption: attributes.caption };
                },
            },
            type: {
                default: 'example',
                parseHTML: element => {
                    return element.getAttribute('type') || 'example';
                },
                renderHTML: attributes => {
                    if (!attributes.type) {
                        return {};
                    }
                    return { type: attributes.type };
                },
            },
            size: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('size') || null;
                },
                renderHTML: attributes => {
                    if (!attributes.size) {
                        return {};
                    }
                    return { size: attributes.size };
                },
            },
            enumType: {
                default: null,
                parseHTML: element => {
                    return element.getAttribute('enumType') || null;
                },
                renderHTML: attributes => {
                    if (!attributes.enumType) {
                        return {};
                    }
                    return { enumType: attributes.enumType };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'zyExample',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyExample', HTMLAttributes, 0];
    },

    addCommands() {
        return {
            insertZyExample:
                (type: ZyExampleType, options = {}) =>
                ({ chain }) => {
                    let commandChain = chain().insertContent({
                        type: 'zyExample',
                        attrs: {
                            caption: options.caption,
                            type,
                            ...generateDefaultExampleAttributes(type),
                        },
                        content: generateDefaultExampleContent(type, options),
                    });

                    if (type === 'table') {
                        commandChain = commandChain.selectDescendantNodeContent('table');
                    }
                    return commandChain.run();
                },
        };
    },
});

const generateDefaultExampleAttributes = (type: ZyExampleType) => {
    const attributes: Record<string, any> = {};
    if (type === 'enum') {
        attributes.enumType = 'Theorem';
    }
    return attributes;
};

const generateDefaultExampleContent = (type: ZyExampleType, options: { rows?: number; cols?: number } = {}) => {
    let content;
    if (type === 'enum') {
        content = [
            {
                type: 'paragraph',
            },
            {
                type: 'blockMath',
                attrs: {
                    latex: 'V = IR',
                },
            },
        ];
    } else if (type === 'example') {
        content = [
            {
                type: 'paragraph',
            },
            {
                type: 'details',
                attrs: {
                    open: true,
                },
                content: [
                    {
                        type: 'detailsSummary',
                        content: [
                            {
                                type: 'text',
                                text: 'Solution',
                            },
                        ],
                    },
                    {
                        type: 'detailsContent',
                        content: [
                            {
                                type: 'paragraph',
                                attrs: {
                                    textAlign: null,
                                },
                            },
                        ],
                    },
                ],
            },
        ];
    } else if (type === 'figure') {
        content = [
            {
                type: 'paragraph',
            },
            {
                type: 'imageUpload',
                attrs: {
                    accept: ACCEPTED_IMAGE_TYPES,
                    limit: 3,
                    maxSize: MAX_FILE_SIZE,
                },
            },
        ];
    } else if (type === 'table') {
        content = options.rows != null && options.cols != null ? buildDefaultTableContent(options.rows, options.cols) : defaultTableContent;
    } else if (type === 'try') {
        content = [
            {
                type: 'paragraph',
            },
        ];
    } else {
        content = [
            {
                type: 'paragraph',
            },
        ];
    }
    return content;
};

export const ZyExampleNode = ZyExampleNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyExampleNodeView);
    },
});

export default ZyExampleNode;
